import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  BoltIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';

const GSTAutomationForCA: React.FC = () => {
  const automationFeatures = [
    {
      icon: BoltIcon,
      title: 'Automated Invoice Data Entry',
      description: 'AI extracts all GST invoice data automatically. Zero manual typing required for invoice processing.',
      timeSaved: '15-20 hours/week',
    },
    {
      icon: ChartBarIcon,
      title: 'Auto-Generated GST Returns',
      description: 'GSTR-1, GSTR-3B auto-generated from processed invoices. One-click export to GST portal.',
      timeSaved: '8-10 hours/month',
    },
    {
      icon: UserGroupIcon,
      title: 'Client Self-Service Portal',
      description: 'Clients upload invoices directly. Automated reminders for pending documents.',
      timeSaved: '5-7 hours/week',
    },
    {
      icon: ClockIcon,
      title: 'Automated Reconciliation',
      description: 'Auto-match purchase vs sales invoices. Flag discrepancies and missing invoices instantly.',
      timeSaved: '10-12 hours/month',
    },
  ];

  const roiMetrics = [
    { metric: '80%', label: 'Time Saved on GST Work' },
    { metric: '95%', label: 'Reduction in Data Errors' },
    { metric: '3x', label: 'More Clients Handled' },
    { metric: '₹2L+', label: 'Annual Cost Savings' },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'GST Automation for Chartered Accountants',
    description: 'Complete guide to automating GST workflow for CA firms. Save 80% time on GST returns, invoice processing, and client management.',
    author: {
      '@type': 'Organization',
      name: 'AICaFriend',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AICaFriend',
    },
  };

  return (
    <>
      <SEO
        title="GST Automation for CA Firms - Save 80% Time on GST Work"
        description="Complete GST automation solution for chartered accountants. Automate invoice processing, GSTR filing, client management, and reconciliation. Save 80% time. Free trial available."
        keywords="gst automation, ca firm automation, gst workflow automation, automated gst filing, gst software for chartered accountants, automate gst returns"
        canonicalUrl="https://aicafriend.com/gst-automation-for-ca"
        structuredData={structuredData}
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
                <BoltIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold">Save 80% Time on GST Work</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Complete GST Automation for
                <span className="gradient-text"> Chartered Accountants</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Automate your entire GST workflow from invoice collection to return filing.
                Handle 3x more clients with the same team. Save 20+ hours per week on repetitive GST tasks.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/pricing"
                  className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg"
                >
                  Automate Your GST Practice
                </Link>
                <Link
                  to="/case-studies"
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-lg"
                >
                  See CA Success Stories
                </Link>
              </div>

              <p className="text-sm text-gray-600">
                Trusted by 500+ CA firms • Free 14-day trial • No credit card required
              </p>
            </div>
          </div>
        </section>

        {/* ROI Metrics Section */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              {roiMetrics.map((item, index) => (
                <div key={index}>
                  <div className="text-4xl md:text-5xl font-bold mb-2">{item.metric}</div>
                  <div className="text-sm md:text-base opacity-90">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Automation Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Gets Automated in Your GST Practice
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                End-to-end automation from invoice collection to GST return filing
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {automationFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="card-hover bg-white p-8 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      Save {feature.timeSaved}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Before vs After Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                GST Practice: Before vs After Automation
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Before */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-red-900 mb-6">❌ Before Automation</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">Chase clients for invoices via calls/emails daily</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">Manual data entry from 100+ invoices per client</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">Frequent errors in GSTIN, HSN codes, tax amounts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">2-3 days to prepare GSTR-1 for one client</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">Manual reconciliation takes hours per client</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-3">•</span>
                    <span className="text-gray-700">Limited to 20-30 GST clients per CA</span>
                  </li>
                </ul>
              </div>

              {/* After */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-green-900 mb-6">✅ After Automation</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Clients upload invoices automatically via portal</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">AI extracts all data automatically in seconds</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">99.5% accuracy with built-in validation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">GSTR-1 ready in 15 minutes with one click</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Auto-reconciliation with smart mismatch detection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Handle 60-100+ GST clients per CA easily</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Calculator Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Calculate Your ROI from GST Automation
              </h2>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">Time Savings Per Month</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex justify-between">
                      <span>Invoice data entry</span>
                      <span className="font-semibold">60-80 hours</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Client follow-ups</span>
                      <span className="font-semibold">20-30 hours</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Return preparation</span>
                      <span className="font-semibold">30-40 hours</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Reconciliation</span>
                      <span className="font-semibold">20-25 hours</span>
                    </li>
                    <li className="flex justify-between border-t border-white/30 pt-3 mt-3">
                      <span className="font-bold">Total Saved</span>
                      <span className="font-bold">130-175 hours</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-4">Cost Savings Per Year</h3>
                  <div className="space-y-4">
                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
                        <span className="font-semibold">Staff Cost Saved</span>
                      </div>
                      <p className="text-2xl font-bold">₹2,00,000 - ₹3,00,000</p>
                      <p className="text-sm opacity-90 mt-1">Equivalent to 1-2 junior staff salaries</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <ChartBarIcon className="h-5 w-5 mr-2" />
                        <span className="font-semibold">Additional Revenue</span>
                      </div>
                      <p className="text-2xl font-bold">₹5,00,000 - ₹10,00,000</p>
                      <p className="text-sm opacity-90 mt-1">From 2-3x more clients you can handle</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/pricing"
                  className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
                >
                  Start Saving Time & Money Today
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Automate Your GST Practice?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join 500+ CA firms using AICaFriend to save 80% time on GST work and grow their practice
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-white text-blue-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-200"
              >
                Schedule Demo
              </Link>
            </div>
            <p className="mt-6 text-sm text-blue-100">
              14-day free trial • No credit card • Full automation included
            </p>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Learn More About GST Automation</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/ai-invoice-processing"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">AI Invoice Processing</h4>
                <p className="text-gray-600 text-sm">How AI automates invoice data extraction</p>
              </Link>
              <Link
                to="/case-studies"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">CA Firm Success Stories</h4>
                <p className="text-gray-600 text-sm">Real results from automated CA practices</p>
              </Link>
              <Link
                to="/resources"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free GST Resources</h4>
                <p className="text-gray-600 text-sm">Guides, templates, and checklists</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GSTAutomationForCA;
